import React, { useState } from 'react';
import { Download } from 'lucide-react';
import { useTranslation } from '../../contexts/TranslationContext';
import { useAuth } from '../../contexts/AuthContext';
import { studentService } from '../../services/studentService';
import { Student } from '../../types';
import toast from 'react-hot-toast';

interface StudentImportFormProps {
  onStudentsImported?: (students: Student[]) => void;
  onClose?: () => void;
  showModal: boolean;
  setShowModal: (show: boolean) => void;
}

export const StudentImportForm: React.FC<StudentImportFormProps> = ({
  onStudentsImported,
  onClose,
  showModal,
  setShowModal
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [importData, setImportData] = useState<string>('');
  const [importPreview, setImportPreview] = useState<any[]>([]);
  const [importLoading, setImportLoading] = useState(false);

  const handleImportStudents = async () => {
    if (!importData.trim()) {
      toast.error(t('students.importErrorNoData'));
      return;
    }

    try {
      setImportLoading(true);
      
      // Parse CSV data
      const lines = importData.trim().split('\n');
      if (lines.length < 2) {
        toast.error(t('students.importErrorInvalidFormat'));
        return;
      }
      
      const headers = lines[0].split(',').map(h => h.trim());
      if (!headers.includes('name') && !headers.includes('Name')) {
        toast.error(t('students.importErrorMissingNameColumn'));
        return;
      }
      
      const dataRows = lines.slice(1);
      
      const studentsToImport = dataRows
        .filter(row => row.trim())
        .map(row => {
          const values = row.split(',').map(v => v.trim());
          const studentData: any = {};
          
          headers.forEach((header, index) => {
            studentData[header.toLowerCase()] = values[index] || '';
          });
          
          return studentData;
        })
        .filter(student => student.name && student.name.trim()); // Only import students with names

      if (studentsToImport.length === 0) {
        toast.error(t('students.importErrorNoValidData'));
        return;
      }

      // Import students
      const importedStudents: Student[] = [];
      for (const studentData of studentsToImport) {
        try {
          // Helper function to clean import values
          const cleanImportValue = (value: string | undefined) => {
            const trimmed = value?.trim();
            return trimmed && trimmed.length > 0 ? trimmed : undefined;
          };
          
          const newStudent = await studentService.addStudent({
            name: studentData.name.trim(),
            grade: cleanImportValue(studentData.grade),
            className: cleanImportValue(studentData.class || studentData.classid || studentData.class_id),
            schoolYear: cleanImportValue(studentData.schoolyear || studentData.school_year),
            status: (studentData.status as 'active' | 'inactive' | 'transferred') || 'active',
            notes: cleanImportValue(studentData.notes),
            teacherId: user?.id || '',
            enrollmentDate: new Date()
          });
          importedStudents.push(newStudent);
        } catch (error) {
          console.error(`Error importing student ${studentData.name}:`, error);
        }
      }

      if (importedStudents.length > 0) {
        toast.success(t('students.importSuccess', { count: importedStudents.length }));
        setShowModal(false);
        setImportData('');
        setImportPreview([]);
        
        // Call the callback to notify parent component
        if (onStudentsImported) {
          onStudentsImported(importedStudents);
        }
      } else {
        toast.error(t('students.importErrorNoStudentsImported'));
      }
    } catch (error) {
      console.error('Error importing students:', error);
      toast.error(t('students.importError'));
    } finally {
      setImportLoading(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setImportData(content);
      
      // Generate preview
      try {
        const lines = content.trim().split('\n');
        const headers = lines[0].split(',').map(h => h.trim());
        const previewRows = lines.slice(1, 6); // Show first 5 rows
        
        const preview = previewRows
          .filter(row => row.trim())
          .map(row => {
            const values = row.split(',').map(v => v.trim());
            const previewData: any = {};
            headers.forEach((header, index) => {
              previewData[header] = values[index] || '';
            });
            return previewData;
          });
        
        setImportPreview(preview);
      } catch (error) {
        console.error('Error parsing preview:', error);
        setImportPreview([]);
      }
    };
    reader.readAsText(file);
  };

  const downloadTemplate = () => {
    const template = 'Name,Grade,Class,SchoolYear,Status,Notes\nJohn Doe,1st Grade,Class A,2024-2025,active,Good student\nJane Smith,2nd Grade,Math 101,Academic Year 2024,active,Excellent progress\nMike Johnson,3rd Grade,Room 205,Fall 2024,inactive,Transferred to another school\nSarah Wilson,4th Grade,Science Lab,2024-2025 School Year,active,\nDavid Brown,5th Grade,Art Studio,Spring 2025,active,New student';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'students_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    setShowModal(false);
    setImportData('');
    setImportPreview([]);
    if (onClose) {
      onClose();
    }
  };

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {t('students.importStudents')}
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('students.importInstructions')}
            </label>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              <button
                onClick={downloadTemplate}
                className="flex items-center justify-center space-x-2 px-3 py-2 text-sm text-blue-600 hover:text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-50 whitespace-nowrap"
              >
                <Download className="w-4 h-4" />
                <span>{t('students.downloadTemplate')}</span>
              </button>
            </div>
          </div>

          {importData && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('students.csvData')}
              </label>
              <textarea
                value={importData}
                onChange={(e) => setImportData(e.target.value)}
                rows={8}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                placeholder="Paste CSV data here or upload a file..."
              />
            </div>
          )}

          {importPreview.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('students.preview')} ({importPreview.length} {t('students.rows')})
              </label>
              <div className="border border-gray-200 rounded-lg overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {Object.keys(importPreview[0] || {}).map((header) => (
                        <th key={header} className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {importPreview.map((row, index) => (
                      <tr key={index}>
                        {Object.values(row).map((value, cellIndex) => (
                          <td key={cellIndex} className="px-2 sm:px-3 py-2 text-xs sm:text-sm text-gray-900 max-w-[120px] sm:max-w-none truncate">
                            {String(value)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
            <h4 className="text-sm font-medium text-blue-800 mb-2">
              {t('students.importFormat')}
            </h4>
            <ul className="text-xs sm:text-sm text-blue-700 space-y-1">
              <li>• <strong>Name</strong>: {t('students.required')}</li>
              <li>• <strong>Grade</strong>: {t('students.optional')} (e.g., "1st Grade", "2nd Grade")</li>
              <li>• <strong>Class</strong>: {t('students.optional')} (free text, e.g., "Class A", "Math 101", "Room 205")</li>
              <li>• <strong>SchoolYear</strong>: {t('students.optional')} (free text, e.g., "2024-2025", "Academic Year 2024", "Fall 2024")</li>
              <li>• <strong>Status</strong>: {t('students.optional')} (active, inactive, transferred)</li>
              <li>• <strong>Notes</strong>: {t('students.optional')}</li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 sm:gap-3 mt-6">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleImportStudents}
            disabled={!importData.trim() || importLoading}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {importLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>{t('students.importing')}</span>
              </>
            ) : (
              <span>{t('students.importStudents')}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
